<template>
  <section class="s-cov" aria-labelledby="insurance-coverage-title">
    <header class="s-cov__header">
      <h2 id="insurance-coverage-title" class="wd-title">
        <span class="s-cov__base">¿Qué tipo de siniestro</span> <span class="hl">deseas reportar?</span>
      </h2>
      <p class="wd-body">
        Selecciona una opción para conocer <br class="br-m" />los requisitos y pasos que debes seguir.
      </p>
    </header>

    <div class="s-cov__cards">
      <InsuranceClaimCard
        v-for="(card, index) in cards"
        :key="card.titleClosed"
        :card="card"
        :default-open="index === 0"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { supportResources } from '../data/supportResources'
import InsuranceClaimCard from './InsuranceClaimCard.vue'
import type { ClaimCardData } from './InsuranceClaimCard.vue'
import fallecimientoIcon from '../../../assets/seguros-figma/ic-fallecimiento.svg'
import invalidezIcon from '../../../assets/seguros-figma/ic-invalidez.svg'
import desempleoIcon from '../../../assets/seguros-figma/ic-desempleo.svg'
import daniosIcon from '../../../assets/seguros-figma/ic-danos.svg'

const checks = (texts: string[]) => texts.map(text => ({ text, check: true }))

const cards: ClaimCardData[] = [
  {
    id: 'life',
    primaryHref: supportResources.lifeClaim,
    secondaryHref: supportResources.insuranceEmail,
    icon: fallecimientoIcon,
    titleClosed: 'Fallecimiento',
    titleOpen: 'Seguro de vida por fallecimiento',
    summary:
      'Consulta los documentos necesarios para presentar una reclamación por fallecimiento del titular o cotitular.',
    description:
      'Cuando ocurre el fallecimiento del titular o cotitular, los beneficiarios pueden iniciar la reclamación del seguro.',
    items: checks([
      'Copia certificada del acta de defunción',
      'Copia simple del certificado de defunción',
      'Copia certificada del acta de nacimiento del asegurado y beneficiarios',
      'Identificación oficial del asegurado y beneficiarios',
      'Comprobante de domicilio del asegurado y beneficiarios',
      'Copia certificada del acta de matrimonio, cuando aplique',
      'Carta del tutor legal, cuando alguno de los beneficiarios sea menor de edad',
      'Actuaciones completas ante el Ministerio Público, en caso de muerte accidental',
      'Reconocimiento del cuerpo',
      'Necropsia',
      'Estudios toxicológicos',
      'Formato de reclamación'
    ]),
    primary: 'Descargar formato de reclamación',
    secondary: 'Enviar documentación'
  },
  {
    id: 'disability',
    primaryHref: supportResources.disabilityClaim,
    secondaryHref: 'tel:+525555119910',
    icon: invalidezIcon,
    titleClosed: 'Invalidez total y permanente',
    titleOpen: 'Seguro por invalidez total y permanente',
    summary: 'Conoce los requisitos para solicitar la evaluación del seguro por invalidez.',
    description:
      'Si el titular o cotitular cuenta con un dictamen de invalidez total y permanente, puede iniciar la solicitud de evaluación del seguro.',
    items: [
      ...checks([
        'Copia del acta de nacimiento',
        'Copia de identificación oficial',
        'Copia del comprobante de domicilio',
        'Original del dictamen de incapacidad',
        'Historial clínico completo',
        'Estudios de laboratorio',
        'Estudios de gabinete',
        'Formato de reclamación'
      ]),
      {
        text: 'El dictamen deberá ser emitido por el IMSS, ISSSTE, SSA o una institución médica legalmente autorizada con especialidad en Medicina del Trabajo.',
        check: false
      }
    ],
    primary: 'Descargar formato de reclamación',
    secondary: 'Hablar con un especialista'
  },
  {
    id: 'unemployment',
    primaryHref: supportResources.unemploymentClaim,
    secondaryHref: 'tel:+525555119910',
    icon: desempleoIcon,
    titleClosed: 'Desempleo',
    titleOpen: 'Seguro por desempleo',
    summary: 'Revisa las condiciones y documentos necesarios para iniciar una reclamación por desempleo.',
    description:
      'Esta cobertura aplica únicamente para personas asalariadas que hayan perdido su empleo y cumplan con las condiciones establecidas en la póliza.<br><br>' +
      '<strong>Debes considerar lo siguiente:</strong><br><br>' +
      '-Debe haber transcurrido al menos un mes desde la pérdida del empleo.<br><br>' +
      '-La aseguradora evaluará la causa de la terminación laboral.<br><br>' +
      '-La cobertura puede aplicar hasta por tres cuotas mensuales.',
    items: checks([
      'Copia del acta de nacimiento',
      'Copia de identificación oficial',
      'Copia del comprobante de domicilio',
      'Contrato laboral',
      'Cálculo del finiquito',
      'Tres recibos de nómina anteriores a la pérdida del empleo',
      'Baja del Seguro Social',
      'Carta indicando el motivo del despido',
      'Formato de reclamación del seguro'
    ]),
    primary: 'Descargar formato de reclamación',
    secondary: 'Hablar con un especialista'
  },
  {
    id: 'property',
    primaryHref: supportResources.insuranceEmail,
    secondaryHref: supportResources.insuranceEmail,
    icon: daniosIcon,
    titleClosed: 'Daños al inmueble',
    titleOpen: 'Reporte de daños al inmueble',
    summary: 'Reporta los daños ocurridos en el inmueble asociado a tu financiamiento.',
    description:
      'Para iniciar el reporte, envía la información del siniestro al correo: <br>' +
      '<u>atencionseguros@hircasa.com.mx</u><br>Datos que debes enviar',
    items: [
      ...checks([
        'Nombre, teléfono y correo de quien reporta el siniestro',
        'Nombre, teléfono y correo de la persona que dará seguimiento',
        'Nombre, teléfono y correo del titular del financiamiento',
        'Fecha del siniestro',
        'Causa del daño',
        'Estimación del monto del daño, si se conoce',
        'Domicilio completo con referencias'
      ]),
      {
        text: '<strong>Incluye preferentemente:</strong><br><br>-Fotografías de los daños<br>-Videos<br>-Documentos relacionados con el incidente',
        check: false
      },
      {
        text: 'El dictamen deberá ser emitido por el IMSS, ISSSTE, SSA o una institución médica legalmente autorizada con especialidad en Medicina del Trabajo.',
        check: false
      }
    ],
    primary: 'Enviar reporte por correo',
    secondary: 'Contactar a seguros'
  }
]
</script>

<style scoped>
.s-cov {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-7);
}
.s-cov__header {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-3);
}
.s-cov__base {
  color: var(--ds-purple-900);
}
.s-cov__cards {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-7);
}

@media (max-width: 1024px) {
  .s-cov {
    gap: var(--ds-space-5);
  }
  .s-cov__cards {
    gap: var(--ds-space-5);
  }
}
</style>
